import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, RotateCw, Activity, Radio, Database, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/maritime";

const Field = ({ label, value, onChange, testid, suffix }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
      {label}
    </label>
    <div className="relative mt-1">
      <Input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-800/60 border-slate-700/60 font-mono text-sm focus-visible:ring-cyan-500/40"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-slate-500">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const STATUS_HEX = { green: "#10B981", amber: "#F59E0B", red: "#EF4444" };

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadHealth = () => api.health().then(setHealth).catch(() => {});

  useEffect(() => {
    api.getSettings().then(setS).catch(() => {});
    loadHealth();
    const i = setInterval(loadHealth, 5000);
    return () => clearInterval(i);
  }, []);

  if (!s) return <div className="min-h-screen bg-[#0B0F19]" />;

  const bbox = s.bboxes[0]; // [[minLat,minLon],[maxLat,maxLon]]
  const setBbox = (i, j, val) => {
    const b = s.bboxes.map((x) => x.map((y) => [...y]));
    b[0][i][j] = parseFloat(val) || 0;
    setS({ ...s, bboxes: b });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.putSettings(s);
      toast.success("Settings saved — worker restarted for new AOI");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const restart = async () => {
    try {
      await api.restartWorker();
      toast.success("Ingestion worker restarted");
      loadHealth();
    } catch {
      toast.error("Restart failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-200">
      <header className="sticky top-0 z-10 h-14 bg-slate-900/90 backdrop-blur-md border-b border-slate-800 px-4 flex items-center gap-3">
        <Link
          to="/"
          data-testid="settings-back"
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800/60 border border-slate-700/60 text-slate-300 hover:text-cyan-400"
        >
          <ArrowLeft className="h-4.5 w-4.5" />
        </Link>
        <h1 className="font-display text-lg font-bold tracking-tight">Settings</h1>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">
        {/* Bounding box */}
        <section
          data-testid="settings-bbox-section"
          className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-5"
        >
          <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-1">
            Geospatial Bounding Box
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            AIS ingestion area. Changing this restarts the stream subscription.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min Latitude" testid="settings-bounding-box-min-lat" value={bbox[0][0]} onChange={(v) => setBbox(0, 0, v)} suffix="°" />
            <Field label="Min Longitude" testid="settings-bounding-box-min-lon" value={bbox[0][1]} onChange={(v) => setBbox(0, 1, v)} suffix="°" />
            <Field label="Max Latitude" testid="settings-bounding-box-max-lat" value={bbox[1][0]} onChange={(v) => setBbox(1, 0, v)} suffix="°" />
            <Field label="Max Longitude" testid="settings-bounding-box-max-lon" value={bbox[1][1]} onChange={(v) => setBbox(1, 1, v)} suffix="°" />
          </div>
        </section>

        {/* Retention */}
        <section className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-5">
          <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-cyan-400 mb-4">
            Data Retention & Processing
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Track Retention" testid="settings-retention-days" value={s.retention_days} onChange={(v) => setS({ ...s, retention_days: parseInt(v) || 0 })} suffix="days" />
            <Field label="Downsample Interval" testid="settings-downsample-seconds" value={s.downsample_seconds} onChange={(v) => setS({ ...s, downsample_seconds: parseInt(v) || 0 })} suffix="sec" />
          </div>
        </section>

        <Button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving…" : "Save Settings"}
        </Button>

        {/* Worker health */}
        <section
          data-testid="worker-health-panel"
          className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
              Worker Health & Status
            </h2>
            <Button
              data-testid="settings-restart-worker-btn"
              onClick={restart}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:text-cyan-400"
            >
              <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Restart
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <HealthCard icon={Radio} label="Connection">
              <span className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: STATUS_HEX[health?.status || "red"] }}
                />
                {health?.connected ? "Connected" : "Down"}
              </span>
            </HealthCard>
            <HealthCard icon={Activity} label="Msgs / min">
              {health?.messages_per_min ?? 0}
            </HealthCard>
            <HealthCard icon={Clock} label="Last msg">
              {timeAgo(health?.last_message)}
            </HealthCard>
            <HealthCard icon={Database} label="Positions">
              {health?.db?.positions ?? 0}
            </HealthCard>
          </div>
          <p className="text-[11px] font-mono text-slate-500 mt-4">
            Provider: {health?.provider || "—"} · Vessels: {health?.db?.vessels ?? 0}
          </p>
        </section>
      </main>
    </div>
  );
}

const HealthCard = ({ icon: Icon, label, children }) => (
  <div className="rounded-lg bg-slate-800/50 border border-slate-700/50 p-3">
    <div className="flex items-center gap-1.5 text-slate-500">
      <Icon className="h-3 w-3" />
      <span className="text-[9px] font-mono uppercase tracking-widest">{label}</span>
    </div>
    <div className="mt-1 font-mono text-sm font-semibold text-white">{children}</div>
  </div>
);
